import React, { useState } from 'react'
import ClientAreaAuditLogModal from '@/components/ClientAreaAuditLogModal/ClientAreaAuditLogModal'
import { FormCheckboxField, FormSelectField } from '@/components/Form/FormField'
import { LoadingOverlay } from '@/components/LoadingOverlay/LoadingOverlay'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { TickmillCompanyTypes } from 'api/Clients/Types/TickmillCompanyTypes'
import { TickmillCompanyDto } from 'api/Identity/Types/Company'
import {
  CreateRebatePolicies,
  RebatePoliciesDto,
} from 'api/Trading-Accounts/Types/RebatePoliciesDto'
import { Form, FormikErrors, FormikProps, withFormik } from 'formik'
import { FormSubmitValues } from 'utils/formValidation'

export interface RebatePoliciesFormValues {
  ibEntity?: number
  clientEntity?: number
  isRelationAllowed?: boolean
}

interface OuterProps {
  isOpen?: boolean
  data: RebatePoliciesDto | undefined
  tickmillCompanies: TickmillCompanyDto[]
  onClose(): void
  onSubmit(values: FormSubmitValues<CreateRebatePolicies>): Promise<void>
}

type RebatePoliciesFormProps = FormikProps<RebatePoliciesFormValues> & OuterProps

const RebatePoliciesFormUI: React.FC<RebatePoliciesFormProps> = (props) => {
  const { handleSubmit, data, errors, touched, tickmillCompanies, isSubmitting } = props
  const [auditLogVisible, setAuditLogVisible] = useState(false)

  const handleSubmitForm = () => {
    handleSubmit()
  }

  return (
    <Dialog open={props.isOpen} onOpenChange={(open) => !open && props.onClose()}>
      <ClientAreaAuditLogModal
        isVisible={auditLogVisible}
        onClose={() => setAuditLogVisible(false)}
        rebatePolicyId={data?.id}
      />
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{data?.id ? 'Edit' : 'New'} Rebate Policies</DialogTitle>
        </DialogHeader>
        <Form>
          <div className='flex flex-col gap-4'>
            <FormSelectField
              name='ibEntity'
              label='IB Entity'
              options={tickmillCompanies.map((company) => ({
                id: company.id,
                name: company.name ?? '',
              }))}
              showSearch
              allowClear
              error={touched.ibEntity ? errors.ibEntity : undefined}
              onChange={(value) =>
                props.setFieldValue('ibEntity', value ? Number(value) : undefined)
              }
            />
            <FormSelectField
              name='clientEntity'
              label='Client Entity'
              options={tickmillCompanies.map((company) => ({
                id: company.id,
                name: company.name ?? '',
              }))}
              showSearch
              allowClear
              error={touched.clientEntity ? errors.clientEntity : undefined}
              onChange={(value) =>
                props.setFieldValue('clientEntity', value ? Number(value) : undefined)
              }
            />
            <FormCheckboxField name='isRelationAllowed' label='Relation Allowed' />
          </div>
        </Form>
        <DialogFooter>
          <Button variant='outline' key='audit-log' onClick={() => setAuditLogVisible(true)}>
            Audit Log
          </Button>
          <Button key='button' type='button' onClick={handleSubmitForm} disabled={!props.isValid}>
            Save
          </Button>
        </DialogFooter>
        <LoadingOverlay isLoading={isSubmitting} />
      </DialogContent>
    </Dialog>
  )
}

export const RebatePoliciesForm = withFormik<OuterProps, RebatePoliciesFormValues>({
  mapPropsToValues: (props) => {
    const { data } = props
    const defaultCompany = getDefaultCompanyId(props.tickmillCompanies)

    return {
      ibEntity: data?.ibEntity?.id ?? defaultCompany,
      clientEntity: data?.clientEntity?.id ?? defaultCompany,
      isRelationAllowed: data?.isRelationAllowed ?? false,
    }
  },
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      setSubmitting(true)
      const data = dataToSubmit(values)
      await props.onSubmit(data)
    } finally {
      setSubmitting(false)
    }
  },
  validate: (values) => {
    const errors: FormikErrors<RebatePoliciesFormValues> = {}

    if (!values.ibEntity) {
      errors.ibEntity = 'Required'
    }

    if (!values.clientEntity) {
      errors.clientEntity = 'Required'
    }

    return errors
  },
  enableReinitialize: false,
  validateOnChange: true,
})(RebatePoliciesFormUI)

const dataToSubmit = (values: RebatePoliciesFormValues) => {
  return {
    ibEntityId: values.ibEntity || 0,
    clientEntityId: values.clientEntity || 0,
    isRelationAllowed: values.isRelationAllowed ?? false,
  }
}

const getDefaultCompanyId = (companies: TickmillCompanyDto[]): TickmillCompanyTypes | undefined => {
  return companies.find((x) => x.id === TickmillCompanyTypes.Tickmill_SC_Company)?.id
}
